import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaUpload, FaTrash, FaPlus, FaDownload, FaEllipsisH, FaChartLine
} from 'react-icons/fa';
import { Smartphone, Wallet, TrendingUp, Users, ChevronLeft, Trophy } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, YAxis, Legend, Cell } from 'recharts';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import TableView from '../TableView';
import { QuickActionsMenu } from '../common';
import EditableTextCell from './EditableTextCell';
import CustomSelect from './CustomSelect';
import ClearMonthModal from './ClearMonthModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import GastosPorCentroCosto from './GastosPorCentroCosto';
import TendenciaAnualModal from './TendenciaAnualModal';
import '../AssetsView.css';
import { SURA_BLUE, SURA_BLUE2, mesesVisibles, serieAnualItem } from './palette';

const CENTROS_COSTO_OPTIONS = [
  '001 - Centro de Distribución', '002 - Share Service', '003 - Presidencia',
  '004 - Gerencia General', '005 - Finanzas', '006 - Gestion Humana',
  '007 - TI', '008 - Mercadeo', '009 - Comercial', '010 - Servicios Generales',
  '011 - Almacen', '012 - Distribucion', '013 - Seguridad Industrial',
  '014 - Calidad', '015 - Supply Chain', '016 - Comercio Exterior',
  '017 - Transporte', '018 - Trade Marketing', '019 - PCP'
];

/* ─── Custom Tooltip ───────────────────────────────────────────── */
const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-xl p-3 min-w-[140px]">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex justify-between gap-3 py-0.5">
          <span className="text-xs text-gray-600">{p.name}</span>
          <span className="text-xs font-bold text-gray-900">${Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
      ))}
    </div>
  );
};

const PresupuestoTelefoniaView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [concepto, setConcepto] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(true);
  const mesesOrden = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
  const mesActual = mesesOrden[new Date().getMonth()];
  const [showModal, setShowModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [mesSeleccionado, setMesSeleccionado] = useState(mesActual);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [filtroDepartamento, setFiltroDepartamento] = useState('');
  const [filtroDeptoTop, setFiltroDeptoTop] = useState('');
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const [lineaAEliminar, setLineaAEliminar] = useState(null);
  const [showComparacion, setShowComparacion] = useState(false);
  const [trendItem, setTrendItem] = useState(null); // { titulo, subtitulo, data }

  const [uploading, setUploading] = useState(false);
  const [fileFactura, setFileFactura] = useState(null);
  const [tasaBcv, setTasaBcv] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [conceptosRes, lineasRes] = await Promise.all([
        axiosInstance.get('/api/presupuestos/conceptos'),
        axiosInstance.get(`/api/presupuestos/telefonia/${id}`)
      ]);
      const currentConcepto = conceptosRes.data.find(c => c.id === parseInt(id));
      setConcepto(currentConcepto);
      setLineas(lineasRes.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleClearMonth = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmClearMonth = async () => {
    try {
      await axiosInstance.delete(`/api/presupuestos/clear/telefonia/${id}?mes=${mesSeleccionado}&anio=${anioSeleccionado}`);
      toast.success(`Datos de ${mesSeleccionado} eliminados correctamente.`);
      setIsDeleteModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar los datos del mes');
    }
  };

  const handleInlineSave = async (lineaId, field, value) => {
    try {
      const res = await axiosInstance.put(`/api/presupuestos/telefonia/linea/${lineaId}`, {
        [field]: value
      });
      if (res.status === 200) {
        setLineas(prev => prev.map(l => l.id === lineaId ? { ...l, [field]: value, es_nuevo: false } : l));
        toast.success("Actualizado correctamente");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error actualizando línea:", error);
      toast.error("Error al actualizar");
      return false;
    }
  };

  const handleDeleteLinea = async (lineaId) => {
    try {
      await axiosInstance.delete(`/api/presupuestos/telefonia/linea/${lineaId}`);
      setLineas(prev => prev.filter(l => l.id !== lineaId));
      toast.success('Línea eliminada');
    } catch (error) {
      toast.error('Error al eliminar la línea');
    } finally {
      setLineaAEliminar(null);
    }
  };

  const handleUploadFactura = async () => {
    if (!fileFactura) {
      toast.error('Por favor, selecciona un archivo PDF');
      return;
    }
    if (!tasaBcv || isNaN(parseFloat(tasaBcv)) || parseFloat(tasaBcv) <= 0) {
      toast.error('Por favor, ingresa una Tasa BCV válida');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('factura', fileFactura);
    formData.append('mes', mesSeleccionado);
    formData.append('anio', anioSeleccionado);
    formData.append('tasa_bcv', parseFloat(tasaBcv));
    formData.append('concepto_id', concepto.id);

    try {
      const loadingToast = toast.loading('Extrayendo datos del PDF...');
      const response = await axiosInstance.post('/api/presupuestos/telefonia/cargar-factura', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.dismiss(loadingToast);
      toast.success(response.data.message || 'Factura procesada con éxito');
      
      setFileFactura(null);
      setTasaBcv('');
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error al procesar factura:', error);
      toast.error(error.response?.data?.error || 'Error al procesar la factura con IA');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadFactura = async () => {
    try {
      const response = await axiosInstance.get(`/api/presupuestos/telefonia/descargar-factura/${mesSeleccionado}/${anioSeleccionado}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Factura_Telefonia_${mesSeleccionado}_${anioSeleccionado}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('No se encontró la factura para este mes o hubo un error al descargarla.');
    }
  };

  const chartData = useMemo(() => {
    return mesesVisibles(anioSeleccionado).map(mes => {
      let gastoTotalMes = 0;
      lineas.forEach(linea => {
        const gastoMes = linea.gastos?.find(g => g.mes === mes && g.anio === anioSeleccionado);
        if (gastoMes) gastoTotalMes += gastoMes.gasto_dolar || 0;
      });
      return { name: mes.substring(0, 3), fullMonth: mes, total: gastoTotalMes };
    });
  }, [lineas, anioSeleccionado]);

  const comparisonData = useMemo(() => {
    const mesesConDatos = chartData.filter(d => d.total > 0);
    if (mesesConDatos.length === 0) return null;
    const ultimoMes = mesesConDatos[mesesConDatos.length - 1];
    const idxUltimo = chartData.findIndex(d => d.name === ultimoMes.name);
    const mesAnterior = idxUltimo > 0 ? chartData[idxUltimo - 1] : null;
    const variacion = mesAnterior && mesAnterior.total > 0
      ? ((ultimoMes.total - mesAnterior.total) / mesAnterior.total) * 100
      : null;
    return {
      ultimoMes,
      mesAnterior,
      variacion,
      barData: [
        ...(mesAnterior ? [{ name: mesAnterior.fullMonth, Gasto: mesAnterior.total }] : []),
        { name: ultimoMes.fullMonth, Gasto: ultimoMes.total }
      ]
    };
  }, [chartData]);

  const gastoTotalAnual = useMemo(() => chartData.reduce((acc, curr) => acc + curr.total, 0), [chartData]);

  /* Gastos por centro de costo (departamento) para el mes/año seleccionado */
  const ccData = useMemo(() => {
    const map = {};
    lineas.forEach(l => {
      const gasto = l.gastos?.find(g => g.mes === mesSeleccionado && g.anio === anioSeleccionado);
      if (!gasto || !gasto.gasto_dolar) return;
      const cc = l.departamento || 'Sin asignar';
      map[cc] = (map[cc] || 0) + Number(gasto.gasto_dolar);
    });
    return Object.entries(map)
      .map(([centro_costo, total]) => ({ centro_costo, total }))
      .sort((a, b) => b.total - a.total);
  }, [lineas, mesSeleccionado, anioSeleccionado]);

  /* Top 10 líneas con mayor gasto en el mes seleccionado (filtrable por departamento) */
  const topLineas = useMemo(() => {
    return lineas
      .filter(l => !filtroDeptoTop || l.departamento === filtroDeptoTop)
      .map(l => {
        const gasto = (l.gastos || []).find(g => g.mes === mesSeleccionado && g.anio === anioSeleccionado);
        return { id: l.id, numero: l.numero, usuario: l.usuario, departamento: l.departamento, total: gasto?.gasto_dolar || 0 };
      })
      .filter(l => l.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [lineas, mesSeleccionado, anioSeleccionado, filtroDeptoTop]);


  const columns = useMemo(() => [
    {
      header: 'Número',
      accessorKey: 'numero',
      cell: ({ row }) => (
        <div className="flex items-center gap-2 font-medium">
          <Smartphone size={14} style={{ color: SURA_BLUE2 }} />
          <EditableTextCell
            value={row.original.numero}
            onSave={(val) => handleInlineSave(row.original.id, 'numero', val)}
          />
          {row.original.es_nuevo && (
            <span className="bg-red-100 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shadow-sm">
              NUEVO
            </span>
          )}
        </div>
      )
    },
    {
      header: 'Usuario Asignado',
      accessorKey: 'usuario',
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.usuario}
          onSave={(val) => handleInlineSave(row.original.id, 'usuario', val)}
        />
      )
    },
    {
      header: 'Departamento',
      accessorKey: 'departamento',
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.departamento}
          onSave={(val) => handleInlineSave(row.original.id, 'departamento', val)}
          options={CENTROS_COSTO_OPTIONS}
        />
      )
    },
    {
      header: 'Plan',
      accessorKey: 'plan',
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.plan}
          onSave={(val) => handleInlineSave(row.original.id, 'plan', val)}
        />
      )
    },
    {
      header: `Consumo (${mesSeleccionado.substring(0,3)})`,
      id: 'consumo_actual',
      cell: ({ row }) => {
        const gasto = row.original.gastos?.find(g => g.mes === mesSeleccionado && g.anio === anioSeleccionado);
        if (!gasto) return <span className="text-gray-300">—</span>;
        return (
          <span className="font-semibold font-mono" style={{ color: SURA_BLUE2 }}>
            ${gasto.gasto_dolar?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
        );
      }
    },
    {
      id: 'acciones',
      header: '',
      size: 50,
      cell: ({ row }) => (
        <div className="quick-actions-container" style={{ position: 'relative' }}>
          <button
            className={`icon-button ${activeActionMenu === row.id ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (activeActionMenu === row.id) {
                setActiveActionMenu(null);
                setMenuAnchorRect(null);
              } else {
                setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                setActiveActionMenu(row.id);
              }
            }}
          >
            <FaEllipsisH color="#111827" />
          </button>

          {activeActionMenu === row.id && (
            <QuickActionsMenu anchorRect={menuAnchorRect}>
              {[
                {
                  icon: FaChartLine,
                  label: 'Ver tendencia anual',
                  color: SURA_BLUE2,
                  onClick: () => {
                    setTrendItem({
                      titulo: row.original.numero || 'Línea',
                      subtitulo: row.original.usuario || row.original.plan || '',
                      data: serieAnualItem(row.original.gastos, anioSeleccionado, 'gasto_dolar')
                    });
                    setActiveActionMenu(null);
                  }
                },
                {
                  icon: FaTrash,
                  label: 'Eliminar Línea',
                  color: '#EF4444',
                  onClick: () => {
                    setLineaAEliminar(row.original.id);
                    setActiveActionMenu(null);
                  }
                }
              ].map((action) => (
                <button key={action.label} className="quick-action-item" onClick={action.onClick}>
                  <span className="action-icon" style={{ color: action.color }}><action.icon /></span>
                  <span className="action-label">{action.label}</span>
                </button>
              ))}
            </QuickActionsMenu>
          )}
        </div>
      )
    }
  ], [mesSeleccionado, anioSeleccionado, activeActionMenu, menuAnchorRect]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: SURA_BLUE }}></div>
      </div>
    );
  }

  if (!concepto) return <div>Concepto no encontrado</div>;

  return (
    <div className="assets-view">

      {/* ── Hero Header ──────────────────────────────────────────── */}
      <div
        className="rounded-2xl mb-6 px-7 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative"
        style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_BLUE2} 100%)` }}
      >
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute right-0 top-0 w-48 h-48 rounded-full opacity-10"
            style={{ background: '#fff', transform: 'translate(30%, -30%)' }} />
          <div className="absolute right-12 bottom-0 w-32 h-32 rounded-full opacity-5"
            style={{ background: '#fff', transform: 'translateY(40%)' }} />
        </div>

        <div className="flex items-center gap-4 z-10">
          <button
            onClick={() => navigate('/presupuestos')}
            className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-medium transition-colors"
          >
            <ChevronLeft size={16} />
            Presupuestos
          </button>
          <div className="w-px h-5 bg-white/20" />
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <Smartphone size={22} color="#fff" />
          </div>
          <div>
            <h2 className="text-white text-xl font-bold m-0 leading-tight">{concepto.nombre}</h2>
            <p className="text-white/60 text-xs m-0 mt-0.5">Control detallado de líneas y planes corporativos</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 z-10">
          <CustomSelect 
            className="px-3 py-1.5 rounded-lg text-sm font-medium outline-none"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
            value={mesSeleccionado} 
            onChange={e => setMesSeleccionado(e.target.value)}
          >
            {mesesOrden.map(m => <option key={m} value={m}>{m}</option>)}
          </CustomSelect>
          <CustomSelect 
            className="px-3 py-1.5 rounded-lg text-sm font-medium outline-none"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
            value={anioSeleccionado} 
            onChange={e => setAnioSeleccionado(parseInt(e.target.value))}
          >
            <option value={2026}>2026</option>
            <option value={2025}>2025</option>
          </CustomSelect>
          <button
            onClick={handleClearMonth}
            className="ml-1 bg-red-500/20 hover:bg-red-500 text-white p-1.5 rounded-lg transition-colors border border-red-500/30 hover:border-red-500 flex items-center justify-center group"
            title="Limpiar datos del mes"
          >
            <FaTrash size={16} className="text-red-300 group-hover:text-white transition-colors" />
          </button>
          <button 
            onClick={handleDownloadFactura}
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            <FaDownload size={12} /> PDF
          </button>
          <button 
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all"
            style={{ background: 'rgba(255,255,255,0.25)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' }}
          >
            <FaUpload size={12} /> Factura Masiva
          </button>
          <span
            className="text-xs font-semibold px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            TELEFONÍA
          </span>
        </div>
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Líneas Activas */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_BLUE }} />
          <div className="flex justify-between items-start mb-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Líneas Activas</p>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#EFF6FF' }}>
              <Users size={17} style={{ color: SURA_BLUE }} />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{lineas.length}</p>
        </div>

        {/* Gasto Total */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_BLUE2 }} />
          <div className="flex justify-between items-start mb-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Gasto Total ({anioSeleccionado})</p>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#EFF6FF' }}>
              <Wallet size={17} style={{ color: SURA_BLUE2 }} />
            </div>
          </div>
          <p className="text-2xl font-bold font-mono text-gray-900">
            ${gastoTotalAnual.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* ── Top 10 + Tendencia Anual ────────────────────────────── */}
      {lineas.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Top 10 — 2 columnas */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Trophy size={14} style={{ color: SURA_BLUE2 }} />
                <h3 className="text-xs font-bold uppercase tracking-wider m-0" style={{ color: SURA_BLUE }}>
                  Top 10 — Líneas con Mayor Gasto
                </h3>
                <span className="text-xs text-gray-400 font-medium">{mesSeleccionado.charAt(0) + mesSeleccionado.slice(1).toLowerCase()} {anioSeleccionado}</span>
              </div>
              <CustomSelect
                className="px-2.5 py-1 rounded-lg text-xs font-medium border-2 border-gray-200 outline-none text-gray-600 bg-white hover:border-gray-300 focus:border-sura-blue transition-colors cursor-pointer"
                value={filtroDeptoTop}
                onChange={e => setFiltroDeptoTop(e.target.value)}
              >
                <option value="">Todos los Departamentos</option>
                {CENTROS_COSTO_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </CustomSelect>
            </div>
            {topLineas.length === 0 ? (
              <p className="text-sm text-gray-400 italic text-center py-4">Sin datos para este filtro en {mesSeleccionado.charAt(0) + mesSeleccionado.slice(1).toLowerCase()}</p>
            ) : (
              <div className="space-y-1.5">
                {topLineas.map((linea, i) => {
                  const maxVal = topLineas[0]?.total || 1;
                  const pct = Math.round((linea.total / maxVal) * 100);
                  return (
                    <div key={linea.id} className="flex items-center gap-3 py-0.5">
                      <div
                        className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                        style={{ background: i < 3 ? SURA_BLUE : '#f1f5f9', color: i < 3 ? '#fff' : '#94a3b8' }}
                      >
                        {i + 1}
                      </div>
                      <div className="w-40 flex-shrink-0 leading-tight">
                        <p className="text-xs font-bold text-gray-800 font-mono m-0 truncate">{linea.numero || '—'}</p>
                        <p className="text-[10px] text-gray-400 m-0 truncate">{linea.usuario || linea.departamento || '—'}</p>
                      </div>
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: i === 0 ? SURA_BLUE : i === 1 ? SURA_BLUE2 : '#93C5FD' }}
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.7, delay: i * 0.06, ease: 'easeOut' }}
                        />
                      </div>
                      <span className="text-xs font-bold text-gray-800 font-mono w-20 text-right flex-shrink-0">
                        ${linea.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tendencia Anual + vs Mes Anterior — 1 columna */}
          <div className="flex flex-col gap-4">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={14} style={{ color: SURA_BLUE2 }} />
                <h3 className="text-xs font-bold uppercase tracking-wider m-0" style={{ color: SURA_BLUE }}>
                  Tendencia Anual
                </h3>
                <span className="text-xs text-gray-400 font-medium ml-auto">{anioSeleccionado}</span>
              </div>
              <div className="h-[170px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradTelefonia" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={SURA_BLUE2} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={SURA_BLUE2} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 9, fill: '#9ca3af' }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={38} />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '11px', boxShadow: '0 4px 6px rgba(0,0,0,0.08)' }}
                      formatter={(value) => [`$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 'Gasto']}
                      labelStyle={{ fontWeight: 'bold', color: '#374151' }}
                    />
                    <Area type="monotone" dataKey="total" stroke={SURA_BLUE2} strokeWidth={2} fill="url(#gradTelefonia)" dot={{ r: 2.5, fill: SURA_BLUE2, strokeWidth: 0 }} activeDot={{ r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Comparación mes anterior */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 relative overflow-hidden hover:shadow-md transition-shadow">
              <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_BLUE }} />
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">vs Mes Anterior</p>
                <TrendingUp size={14} style={{ color: SURA_BLUE2 }} />
              </div>
              {comparisonData ? (
                <>
                  <div className="flex items-end gap-2">
                    <div className="text-center flex-1">
                      <p className="text-[10px] text-gray-400 m-0 mb-1">{comparisonData.mesAnterior?.name || '—'}</p>
                      <p className="text-xs font-semibold text-gray-500 m-0">
                        {comparisonData.mesAnterior ? `$${comparisonData.mesAnterior.total.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
                      </p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="text-[10px] text-gray-400 m-0 mb-1">{comparisonData.ultimoMes.name}</p>
                      <p className="text-xs font-bold text-gray-800 m-0">
                        ${comparisonData.ultimoMes.total.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </p>
                    </div>
                    {comparisonData.variacion !== null && (
                      <div className={`text-center flex-1 px-1 py-1 rounded-lg ${
                        comparisonData.variacion > 5 ? 'bg-red-50 text-red-600' :
                        comparisonData.variacion < -5 ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-500'
                      }`}>
                        <p className="text-[10px] m-0 font-bold whitespace-nowrap">
                          {comparisonData.variacion > 0 ? '▲' : '▼'} {Math.abs(comparisonData.variacion).toFixed(1)}%
                        </p>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setShowComparacion(true)}
                    className="mt-2 text-xs font-medium hover:underline"
                    style={{ color: SURA_BLUE2 }}
                  >
                    Ver detalle →
                  </button>
                </>
              ) : (
                <p className="text-xs text-gray-400 mt-2">Sin datos suficientes</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Gastos por Centro de Costo ─────────────────────────── */}
      <div className="mb-6">
        <GastosPorCentroCosto
          data={ccData}
          subtitle={`${mesSeleccionado.charAt(0) + mesSeleccionado.slice(1).toLowerCase()} ${anioSeleccionado}`}
        />
      </div>

      {/* ── Tabla ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <TableView
          data={[...(filtroDepartamento ? lineas.filter(l => l.departamento === filtroDepartamento) : lineas)].sort((a, b) => (a.es_nuevo === b.es_nuevo ? 0 : a.es_nuevo ? -1 : 1))}
          columns={columns} 
          searchPlaceholder="Buscar por número, usuario, plan..."
          customActions={
            <CustomSelect
              className="px-3 py-1.5 rounded-xl text-sm font-medium border-2 border-gray-200 outline-none text-gray-600 bg-white hover:border-gray-300 focus:border-sura-blue transition-colors cursor-pointer"
              value={filtroDepartamento}
              onChange={e => setFiltroDepartamento(e.target.value)}
            >
              <option value="">Todas las Sedes / Dptos</option>
              {CENTROS_COSTO_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </CustomSelect>
          }
        />
      </div>

      {/* ── Modal Cargar Factura ──────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div
              className="px-6 py-5 flex justify-between items-center"
              style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_BLUE2} 100%)` }}
            >
              <h3 className="text-white font-bold m-0 flex items-center gap-2">
                <FaUpload size={14} /> Cargar Factura ({mesSeleccionado})
              </h3>
              <button
                onClick={() => { if (!uploading) { setFileFactura(null); setTasaBcv(''); setShowModal(false); }}}
                className="text-white/70 hover:text-white text-lg font-bold"
              >✕</button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Tasa BCV Actual <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400 font-bold text-sm">Bs</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ingrese la tasa BCV"
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors"
                    value={tasaBcv}
                    onChange={(e) => setTasaBcv(e.target.value)}
                    disabled={uploading}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Los montos en Bs se convertirán a USD usando esta tasa.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Archivo de Factura <span className="text-red-500">*</span>
                </label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center transition-all relative ${
                    fileFactura ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50 hover:border-blue-300'
                  }`}
                >
                  <input 
                    type="file" 
                    accept="application/pdf"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                    onChange={(e) => {
                      if(e.target.files && e.target.files.length > 0) {
                        setFileFactura(e.target.files[0]);
                      }
                    }}
                    disabled={uploading}
                  />
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                    style={{ background: fileFactura ? '#EFF6FF' : '#F9FAFB' }}
                  >
                    <FaUpload style={{ color: fileFactura ? SURA_BLUE : '#9CA3AF' }} size={20} />
                  </div>
                  {fileFactura ? (
                    <div className="text-center">
                      <p className="font-bold text-sm m-0" style={{ color: SURA_BLUE }}>{fileFactura.name}</p>
                      <p className="text-gray-400 text-xs mt-1">Haz clic para cambiar el archivo</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-gray-700 font-medium text-sm mb-1">Selecciona un archivo PDF</p>
                      <p className="text-gray-400 text-xs">El sistema procesará los cobros automáticamente.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button 
                onClick={() => { if (!uploading) { setFileFactura(null); setTasaBcv(''); setShowModal(false); }}}
                className="btn-secondary"
                disabled={uploading}
              >
                Cancelar
              </button>
              <button 
                onClick={handleUploadFactura} 
                disabled={uploading || !fileFactura || !tasaBcv}
                className="px-5 py-2 rounded-lg text-sm font-semibold text-white flex items-center gap-2 transition-all disabled:opacity-50"
                style={{ background: SURA_BLUE }}
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Procesando...
                  </>
                ) : 'Analizar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Comparación ────────────────────────────────── */}
      {showComparacion && comparisonData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
            <div
              className="px-6 py-5 flex justify-between items-center"
              style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_BLUE2} 100%)` }}
            >
              <h3 className="text-white font-bold m-0 flex items-center gap-2">
                <TrendingUp size={16} /> Comparativa de Gasto
              </h3>
              <button 
                onClick={() => setShowComparacion(false)}
                className="text-white/70 hover:text-white text-lg font-bold"
              >✕</button>
            </div>

            <div className="p-6">
              <div className="flex flex-col sm:flex-row justify-between mb-6">
                <div className="mb-4 sm:mb-0">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Periodo Actual</p>
                  <p className="text-2xl font-bold font-mono text-gray-900 m-0">
                    {comparisonData.ultimoMes.fullMonth}: ${comparisonData.ultimoMes.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                {comparisonData.mesAnterior && (
                  <div className="text-right">
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Periodo Anterior</p>
                    <p className="text-lg font-medium text-gray-500 m-0">
                      {comparisonData.mesAnterior.fullMonth}: ${comparisonData.mesAnterior.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                    {comparisonData.variacion !== null && (
                      <p className={`text-sm font-bold mt-1 ${comparisonData.variacion > 0 ? 'text-red-500' : 'text-green-600'}`}>
                        {comparisonData.variacion > 0 ? '▲' : '▼'} {Math.abs(comparisonData.variacion).toFixed(2)}% vs mes anterior
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={comparisonData.barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12, fontWeight: 500 }} />
                    <YAxis 
                      tickFormatter={(value) => `$${value}`} 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6B7280', fontSize: 12 }}
                      width={60}
                    />
                    <Tooltip
                      content={<CustomBarTooltip />}
                      cursor={{ fill: '#F8FAFC' }}
                    />
                    <Bar dataKey="Gasto" radius={[6, 6, 0, 0]} maxBarSize={60}>
                      {comparisonData.barData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={index === comparisonData.barData.length - 1 ? SURA_BLUE : SURA_BLUE2}
                          opacity={index === comparisonData.barData.length - 1 ? 1 : 0.45}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      <ClearMonthModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmClearMonth}
        mes={mesSeleccionado}
        anio={anioSeleccionado}
        tipo="Telefonía"
      />

      <ConfirmDeleteModal
        isOpen={lineaAEliminar !== null}
        onClose={() => setLineaAEliminar(null)}
        onConfirm={() => handleDeleteLinea(lineaAEliminar)}
        title="Eliminar línea"
        message="¿Seguro que deseas eliminar esta línea? Se ocultará de la lista pero su historial de gastos se conserva."
      />

      <TendenciaAnualModal
        isOpen={trendItem !== null}
        onClose={() => setTrendItem(null)}
        titulo={trendItem?.titulo}
        subtitulo={trendItem?.subtitulo}
        anio={anioSeleccionado}
        data={trendItem?.data || []}
      />
    </div>
  );
};

export default PresupuestoTelefoniaView;
